import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { ZoneFormData, ZoneArea } from "../../types/zone";

// Add type for handleZoneAreaChange
type ZoneAreaField = 'serialNo' | 'areaCode';

export default function ZoneForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ZoneFormData>({
    zoneName: "",
    description: "",
    status: true,
    remarks: "",
    zoneAreas: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditMode) {
      fetchZoneData();
    }
  }, [id]);

  const fetchZoneData = async () => {
    try {
      const response = await axiosInstance.get(`/zones/${id}`);
      const data = response.data;
      setFormData({
        zoneName: data.zoneName ?? "",
        description: data.description ?? "",
        status: data.status ?? true,
        remarks: data.remarks ?? "",
        zoneAreas: (data.zoneAreas ?? []).map((area: ZoneArea, index: number) => ({
          id: area.id,
          serialNo: area.serialNo ?? index + 1,
          areaCode: area.areaCode ?? (area as { postCode?: string }).postCode ?? "",
        })),
      });
    } catch (error) {
      setError("Failed to fetch zone data");
      console.error("Error fetching zone:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        description: formData.description,
        status: formData.status,
        remarks: formData.remarks,
        zoneAreas: formData.zoneAreas.map((area) => ({
          postCode: area.areaCode,
        })),
      };

      if (isEditMode) {
        await axiosInstance.put(`/zones/${id}`, submitData);
      } else {
        await axiosInstance.post("/zones", submitData);
      }
      navigate("/zones");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save zone");
      console.error("Error saving zone:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddZoneArea = () => {
    setFormData(prev => ({
      ...prev,
      zoneAreas: [
        ...prev.zoneAreas,
        {
          serialNo: prev.zoneAreas.length + 1,
          areaCode: "",
          // Don't set zoneId for new areas
        } as ZoneArea
      ]
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        name === "status"
          ? value === "true"
          : type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : value
    }));
  };

  const handleZoneAreaChange = (index: number, field: ZoneAreaField, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      zoneAreas: prev.zoneAreas.map((area, i) =>
        i === index ? { ...area, [field]: value } : area
      )
    }));
  };

  const handleRemoveZoneArea = (index: number) => {
    setFormData(prev => ({
      ...prev,
      zoneAreas: prev.zoneAreas.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Zone" : "Add Zone"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <InputWithLabel
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status.toString()}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Zone Areas</h3>
              <button
                type="button"
                onClick={handleAddZoneArea}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add Area
              </button>
            </div>

            {formData.zoneAreas.map((area, index) => (
              <div key={index} className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 p-4 border rounded-lg">
                <InputWithLabel
                  label="Serial No"
                  name={`serialNo-${index}`}
                  type="number"
                  value={area.serialNo.toString()}
                  onChange={(e) => handleZoneAreaChange(index, 'serialNo', parseInt(e.target.value))}
                  required
                />
                <InputWithLabel
                  label="Area Code"
                  name={`areaCode-${index}`}
                  value={area.areaCode}
                  onChange={(e) => handleZoneAreaChange(index, 'areaCode', e.target.value)}
                  required
                />
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => handleRemoveZoneArea(index)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/zones")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 